
import { Link } from 'react-router-dom';
import { LogIn, User } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLocation } from 'react-router-dom';

const NavbarUserMenu = () => {
  const { user, isAdmin, isCompany, logout, loading } = useAuth();
  const [profile, setProfile] = useState<any>(null);
  const isMobile = useIsMobile();
  const location = useLocation();
  
  // Get user initials for avatar fallback
  const getInitials = (name: string) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  useEffect(() => {
    // If user is logged in and auth state is not loading, try to fetch their profile
    const fetchProfile = async () => {
      if (user && !loading) {
        try {
          console.log("NavbarUserMenu: Fetching profile for user:", user.id);
          const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .maybeSingle();
            
          if (error) {
            console.error("NavbarUserMenu: Error fetching profile:", error);
          } else if (data) {
            console.log("NavbarUserMenu: Profile fetched successfully");
            setProfile(data);
          }
        } catch (error) {
          console.error('Error fetching profile:', error);
        }
      }
    };
    
    fetchProfile();
  }, [user, loading]);
  
  const handleLogout = async () => {
    await logout();
  };
  
  console.log("NavbarUserMenu render state:", { 
    loading, 
    user: !!user, 
    isCompany, 
    email: user?.email,
    metadata: user?.user_metadata 
  });
  
  // If auth is still loading, show minimal UI
  if (loading) {
    return (
      <div className="flex items-center">
        <div className="w-8 h-8 rounded-full bg-gray-100"></div>
      </div>
    );
  }
  
  if (user) {
    const fullName = profile?.full_name || user.user_metadata?.full_name || user.email?.split('@')[0] || 'User';
    const avatarUrl = profile?.avatar_url || user.user_metadata?.avatar_url;
    const initials = getInitials(fullName);
    
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="rounded-full w-9 h-9 p-0">
            <Avatar className="h-8 w-8">
              {avatarUrl && <AvatarImage src={avatarUrl} alt={fullName} />}
              <AvatarFallback className="bg-redflags-red/10 text-redflags-red">
                {initials}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div className="flex flex-col">
              <span>{fullName}</span>
              <span className="text-xs text-muted-foreground">{user.email}</span>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {isCompany && (
            <DropdownMenuItem asChild>
              <Link to="/company-dashboard" className="cursor-pointer">
                Company Dashboard
              </Link>
            </DropdownMenuItem>
          )}
          
          {isAdmin && (
            <DropdownMenuItem asChild>
              <Link to="/admin/dashboard" className="cursor-pointer">
                Admin Dashboard
              </Link>
            </DropdownMenuItem>
          )}
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600">
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
  
  // Adjusted layout for non-authenticated users to be on one line for all mobile screens
  return (
    <div className={`flex items-center ${isMobile ? 'gap-1' : 'gap-2'}`}>
      <Link 
        to="/login" 
        className={`text-redflags-gray hover:text-redflags-dark ${isMobile ? 'px-2' : 'px-3'} py-2 rounded-md text-sm transition-colors duration-300`}
      >
        Log in
      </Link>
      <Link 
        to="/signup" 
        className={`bg-redflags-red text-white hover:bg-redflags-red/90 ${isMobile ? 'px-2' : 'px-3'} py-2 rounded-md text-sm transition-colors duration-300`}
      >
        Sign up
      </Link>
    </div>
  );
};

export default NavbarUserMenu;
